import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';

// Contact Support modal — the single shared support surface used by the
// global Header, the NavSidebar footer, and the DiagramEditor ribbon's Help
// tab (which owns it there because the global Header is suppressed on
// that route).
//
// Built on CgDialog (Radix) so it matches the app's modal precedent —
// focus trap, ESC, scroll lock, and outside-click come for free.
//
// Non-whitelabel: a two-card chooser between "Book a Support Call"
// (inline Calendly) and "Email Us". Whitelabel instances have no
// Calendly booking, so they skip the chooser and land straight on the
// email panel.
const CALENDLY_URL = 'https://calendly.com/d/cspm-4ff-p96/carbon-glance-support?hide_gdpr_banner=1';

const ensureCalendlyScriptLoaded = () => {
  if (document.getElementById('calendly-widget-script')) return;
  const script = document.createElement('script');
  script.id = 'calendly-widget-script';
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://assets.calendly.com/assets/external/widget.js';
  document.body.appendChild(script);
};

const ensureCalendlyStylesLoaded = () => {
  if (document.getElementById('calendly-widget-css')) return;
  const link = document.createElement('link');
  link.id = 'calendly-widget-css';
  link.rel = 'stylesheet';
  link.href = 'https://assets.calendly.com/assets/external/widget.css';
  document.head.appendChild(link);
};

export default function ContactSupportModal({
  show,
  onHide,
  supportEmail,
  brandName,
  isWhitelabel = false,
  primaryColor = '#00b894',
}) {
  // Whitelabel has no call option, so its only mode is email — start
  // there directly instead of on the (single-card) chooser.
  const initialMode = isWhitelabel ? 'email' : null;
  const [contactMode, setContactMode] = useState(initialMode);
  const [emailCopied, setEmailCopied] = useState(false);
  const calendlyContainerRef = useRef(null);

  // Reset the picker each time the modal closes so the next open starts
  // on the chooser (or, for whitelabel, back on the email panel).
  useEffect(() => {
    if (!show) setContactMode(initialMode);
  }, [show, initialMode]);

  useEffect(() => {
    if (!show || isWhitelabel) return;
    ensureCalendlyScriptLoaded();
    ensureCalendlyStylesLoaded();
  }, [show, isWhitelabel]);

  const initCalendlyInline = useCallback(() => {
    if (!window.Calendly || !calendlyContainerRef.current) return;
    calendlyContainerRef.current.innerHTML = '';
    window.Calendly.initInlineWidget({
      url: CALENDLY_URL,
      parentElement: calendlyContainerRef.current,
    });
  }, []);

  // Calendly's widget script loads async; poll briefly until it's
  // available, then init exactly once.
  const startCalendlyInit = useCallback(() => {
    if (contactMode !== 'call') return;
    let attempts = 0;
    const maxAttempts = 25; // ~5s max
    const interval = setInterval(() => {
      attempts++;
      if (!show || contactMode !== 'call') {
        clearInterval(interval);
        return;
      }
      if (window.Calendly && calendlyContainerRef.current) {
        clearInterval(interval);
        initCalendlyInline();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 200);
  }, [show, contactMode, initCalendlyInline]);

  useEffect(() => {
    if (show && contactMode === 'call') startCalendlyInit();
  }, [show, contactMode, startCalendlyInit]);

  const handleCopySupportEmail = async () => {
    if (!supportEmail) return;
    try {
      await navigator.clipboard.writeText(supportEmail);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch (err) {
      try {
        const temp = document.createElement('input');
        temp.value = supportEmail;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 1500);
      } catch (_) {}
    }
  };

  const name = brandName || '';
  const emailBody =
    `Hi ${name} Support,\n\n` +
    'Please describe your issue or question here.\n\n' +
    'Helpful details (optional):\n' +
    '- Organization Name:\n' +
    '- Page or Feature:\n' +
    '- Steps to Reproduce:\n' +
    '- Screenshots/Links:\n\n' +
    'Thanks!';

  return (
    <CgDialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
      title="Contact Support"
      description={
        isWhitelabel
          ? `Reach the ${name} support team by email.`
          : 'Book a call with our team or reach us by email.'
      }
      width={contactMode === 'call' ? 960 : 720}
    >
      {contactMode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <button
            type="button"
            className="text-left p-5 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': primaryColor }}
            onClick={() => setContactMode('call')}
          >
            <div className="flex items-start gap-3">
              <span
                className="mt-0.5 shrink-0"
                style={{ color: primaryColor }}
              >
                <FontAwesomeIcon icon={faIcons.faPhone} />
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">Book a Support Call</div>
                <div className="text-sm leading-relaxed text-slate-600 mt-1.5">
                  Schedule a 15–30 min call with our team. Best for walkthroughs, complex issues, or
                  when screen sharing would help.
                </div>
              </div>
            </div>
          </button>

          <button
            type="button"
            className="text-left p-5 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': primaryColor }}
            onClick={() => setContactMode('email')}
          >
            <div className="flex items-start gap-3">
              <span
                className="mt-0.5 shrink-0"
                style={{ color: primaryColor }}
              >
                <FontAwesomeIcon icon={faIcons.faEnvelope} />
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">Email Us</div>
                <div className="text-sm leading-relaxed text-slate-600 mt-1.5 break-words">
                  Send details to {supportEmail}. Ideal for quick questions or when attaching
                  screenshots/logs.
                </div>
              </div>
            </div>
          </button>
        </div>
      )}

      {contactMode === 'call' && (
        <>
          <div className="bg-white rounded-lg overflow-hidden">
            {/* Cap against the viewport so the embed never overflows the
                dialog's max-h-[90vh] and force a nested scrollbar. */}
            <div
              ref={calendlyContainerRef}
              style={{ minWidth: '320px', height: 'min(760px, calc(90vh - 180px))' }}
            />
          </div>
          <div className="mt-3 text-sm leading-relaxed text-slate-500">
            Having trouble with the embed?{' '}
            <a
              className="underline"
              style={{ color: primaryColor }}
              href={CALENDLY_URL.replace('?hide_gdpr_banner=1', '')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in a new tab
            </a>
            <button
              type="button"
              className="ml-3 text-slate-500 hover:text-slate-700 underline"
              onClick={() => setContactMode(null)}
            >
              Choose another contact method
            </button>
          </div>
        </>
      )}

      {contactMode === 'email' && (
        <div>
          <p className="text-slate-700 leading-relaxed mb-2">Send your message to:</p>
          <div className="flex items-center flex-wrap gap-2 mb-4">
            <span className="font-medium text-slate-800 break-all">{supportEmail}</span>
            <button
              type="button"
              className="p-1.5 rounded hover:bg-slate-100 shrink-0"
              onClick={handleCopySupportEmail}
              aria-label="Copy support email"
              title="Copy email"
            >
              <FontAwesomeIcon
                icon={faIcons.faCopy}
                className="text-slate-500"
              />
            </button>
            {emailCopied && <span className="text-xs text-slate-500">Copied</span>}
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words mb-4">
            {`Subject: Support Request from ${name}\n\n${emailBody}`}
          </div>
          <div className="flex items-center flex-wrap gap-3">
            <a
              className="inline-block text-white px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
              href={
                `mailto:${supportEmail}?subject=` +
                encodeURIComponent(`Support Request from ${name}`) +
                '&body=' +
                encodeURIComponent(emailBody)
              }
            >
              Compose Email
            </a>
            {!isWhitelabel && (
              <button
                type="button"
                className="text-slate-500 hover:text-slate-700 underline"
                onClick={() => setContactMode(null)}
              >
                Choose another contact method
              </button>
            )}
          </div>
        </div>
      )}
    </CgDialog>
  );
}
